import { useState, useEffect, useContext, createContext, useCallback, useMemo } from 'react';
import { authAPI } from '../services/api';

// Create Auth Context
const AuthContext = createContext();

// Auth Provider Component
export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(localStorage.getItem('authToken'));

  const initializeAuth = async () => {
    try {
      setLoading(true);
      const storedToken = localStorage.getItem('authToken');
      const storedUserData = localStorage.getItem('userData');

      if (storedToken) {
        // Verify token with backend (always refresh role / isAdmin from DB)
        try {
          const response = await authAPI.getProfile();
          setUser(response.user);
          localStorage.setItem('userData', JSON.stringify(response.user));
          setIsAuthenticated(true);
          setToken(storedToken);
        } catch (error) {
          // Token is invalid, clear storage
          console.error('Token validation failed:', error);
          await logout();
        }
      } else {
        if (storedUserData) localStorage.removeItem('userData');
        setUser(null);
        setIsAuthenticated(false);
        setToken(null);
      }
    } catch (error) {
      console.error('Auth initialization error:', error);
      await logout();
    } finally {
      setLoading(false);
    }
  };

  // Initialize auth state on mount (deduped via authAPI.getProfile inflight coalescing)
  useEffect(() => {
    initializeAuth();
  }, []);

  const login = (userData, authToken) => {
    localStorage.setItem('authToken', authToken);
    localStorage.setItem('userData', JSON.stringify(userData));
    setUser(userData);
    setToken(authToken);
    setIsAuthenticated(true);
  };

  const logout = async () => {
    try {
      // Call logout endpoint if token exists
      if (token) {
        await authAPI.logout();
      }
    } catch (error) {
      // Continue with logout even if API call fails
      console.error('Logout API error:', error);
    } finally {
      // Clear local state and storage
      localStorage.removeItem('authToken');
      localStorage.removeItem('token');
      localStorage.removeItem('userData');
      setUser(null);
      setToken(null);
      setIsAuthenticated(false);
    }
  };

  const updateUser = useCallback((updatedUserData) => {
    setUser(updatedUserData);
    localStorage.setItem('userData', JSON.stringify(updatedUserData));
  }, []);

  const refreshUser = useCallback(async () => {
    const storedToken = localStorage.getItem('authToken');
    if (!storedToken) return null;
    try {
      const response = await authAPI.getProfile();
      updateUser(response.user);
      return response.user;
    } catch (error) {
      console.error('Failed to refresh user:', error);
      await logout();
      throw error;
    }
  }, [updateUser]);

  const value = useMemo(
    () => ({
      user,
      isAuthenticated,
      loading,
      token,
      login,
      logout,
      updateUser,
      refreshUser
    }),
    [user, isAuthenticated, loading, token, updateUser, refreshUser]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

// Hook to use auth context
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

// Higher-order component for protected routes
export const withAuth = (Component) => {
  return function AuthenticatedComponent(props) {
    const { isAuthenticated, loading } = useAuth();

    if (loading) {
      return (
        <div className="min-h-screen flex items-center justify-center">
          <div className="text-center">
            <div className="w-8 h-8 border-4 border-[#FF5E4C] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-gray-600">Loading...</p>
          </div>
        </div>
      );
    }

    if (!isAuthenticated) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-[#FAF7F2]">
          <div className="text-center p-8">
            <h2 className="text-xl font-medium text-gray-800 mb-4">Authentication Required</h2>
            <p className="text-gray-600 mb-4">Please log in to access this page.</p>
            <button 
              onClick={() => window.location.href = '/'}
              className="bg-[#FF5E4C] text-white px-6 py-2 rounded-lg hover:bg-[#FF4A36] transition-colors"
            >
              Go to Home
            </button>
          </div>
        </div>
      );
    }

    return <Component {...props} />;
  };
};
